<script setup lang="ts">
import { onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAppTheme, useColors } from '@/composables/ui'

import { useSetupStatus } from '../../composables/useSetupStatus'
import { authService } from '../../services/auth'

const router = useRouter()
const { getColor } = useColors()
const { setupStatus, completionPercent, loading, fetchSetupStatus } = useSetupStatus()

// Check if there's a selected shop
const hasSelectedShop = computed(() => {
  return authService.getShopId() !== null
})

const progressText = computed(() => {
  const percent = completionPercent.value
  if (percent >= 80) {
    return "You're doing great!"
  } else if (percent >= 50) {
    return 'Just a few steps left'
  } else {
    return 'Get started with setup'
  }
})

const handleContinueSetup = () => {
  router.push({ name: 'setup-checklist' })
}

onMounted(async () => {
  await fetchSetupStatus()
})
</script>

<template>
  <!-- Hide widget when no shop selected, setup is complete (100%), or loading -->
  <div v-if="hasSelectedShop && !loading && completionPercent < 100" class="setup-progress-widget px-4 pb-4">
    <v-card
      :style="{
        backgroundColor: getColor('backgroundElement'),
      }"
      class="setup-progress-card"
    >
      <v-card-text class="space-y-4">
        <div class="space-y-2">
          <h3 class="text-sm font-semibold" :style="{ color: getColor('textPrimary') }">Setup Progress</h3>
          <p class="text-xs" :style="{ color: getColor('textSecondary') }">
            {{ progressText }}
          </p>
        </div>

        <!-- Progress Bar -->
        <div v-if="!loading" class="space-y-2">
          <v-progress-linear :model-value="completionPercent" color="primary" :size="6" />
          <div class="flex justify-between items-center">
            <span class="text-xs font-medium" :style="{ color: getColor('textSecondary') }">
              {{ completionPercent }}% complete
            </span>
          </div>
        </div>

        <!-- Loading State -->
        <div v-else class="space-y-2">
          <v-progress-linear :model-value="0" color="primary" :size="6" />
          <div class="flex justify-between items-center">
            <span class="text-xs" :style="{ color: getColor('textSecondary') }">Loading...</span>
          </div>
        </div>

        <!-- Continue Setup Button -->
        <v-btn
          v-if="!loading && completionPercent < 100"
          color="primary"
          size="small"
          class="w-full"
          @click="handleContinueSetup"
        >
          Continue Setup →
        </v-btn>
      </v-card-text>
    </v-card>
  </div>
</template>

<style scoped>
.setup-progress-widget {
  margin-top: auto;
}

.setup-progress-card {
  border-radius: 8px;
}
</style>
